/**
 * VendorPortalProfileService
 * Service for vendor profile management (view and update own profile).
 * Route: api/vendor-portal/profile
 */
import { VendorPortalBaseService } from './VendorPortalBaseService';
/**
 * VendorPortalProfileService
 * Handles vendor profile operations for the vendor portal.
 * Maps to /api/vendor-portal/profile endpoint
 */
export class VendorPortalProfileService extends VendorPortalBaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorPortalProfileService.DEFAULT_SERVICE_PATH,
            basePath: config?.basePath ?? VendorPortalProfileService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET PROFILE ====================
    /**
     * Get the current vendor's profile
     * GET /api/vendor-portal/profile
     */
    async getProfile() {
        const response = await this.getProfileRaw();
        if (!response.data) {
            throw new Error('Failed to retrieve vendor profile');
        }
        return response.data;
    }
    /**
     * Get the current vendor's profile (raw response)
     */
    getProfileRaw() {
        return this.get();
    }
    // ==================== UPDATE PROFILE ====================
    /**
     * Update the current vendor's profile
     * PUT /api/vendor-portal/profile
     */
    async updateProfile(data) {
        const response = await this.updateProfileRaw(data);
        if (!response.data) {
            throw new Error('Failed to update vendor profile');
        }
        return response.data;
    }
    /**
     * Update the current vendor's profile (raw response)
     */
    updateProfileRaw(data) {
        return this.put('', data);
    }
    // ==================== GET SUMMARY ====================
    /**
     * Get the current vendor's summary with compliance and metrics
     * GET /api/vendor-portal/profile/summary
     */
    async getSummary() {
        const response = await this.getSummaryRaw();
        if (!response.data) {
            throw new Error('Failed to retrieve vendor summary');
        }
        return response.data;
    }
    /**
     * Get the current vendor's summary (raw response)
     */
    getSummaryRaw() {
        return this.get('summary');
    }
    // ==================== VENDOR CONTEXT (no X-Vendor-ID required) ====================
    /**
     * Get the current user's vendor context (vendor IDs and primary vendor).
     * This endpoint does NOT require X-Vendor-ID header — it resolves from
     * the authenticated user's JWT claims and the UserVendors table.
     * Used as a recovery mechanism when vendorId is lost from storage.
     * GET /api/vendor-portal/context
     */
    async getVendorContext() {
        // Use http.get directly (not this.get) to bypass ensureVendorHeader()
        // since this endpoint is specifically for when vendorId is unknown
        const contextPath = `/${this.servicePath}/api/vendor-portal/context`;
        console.log('[VendorPortalProfileService] getVendorContext - calling:', contextPath);
        const response = await this.http.get(contextPath);
        if (!response.data) {
            throw new Error('Failed to retrieve vendor context');
        }
        return response.data;
    }
    // ==================== SUBMIT FOR REVIEW ====================
    /**
     * Submit vendor for review (transitions from Draft to Submitted)
     * POST /api/vendor-portal/profile/submit
     */
    async submitForReview() {
        const response = await this.submitForReviewRaw();
        if (!response.data) {
            throw new Error('Failed to submit vendor for review');
        }
        return response.data;
    }
    /**
     * Submit vendor for review (raw response)
     */
    submitForReviewRaw() {
        return this.post('submit', {});
    }
    // ==================== LOGO MANAGEMENT ====================
    /**
     * Update the vendor's logo URL
     * PUT /api/vendor-portal/profile/logo
     */
    async updateLogo(url) {
        const response = await this.updateLogoRaw(url);
        if (!response.data) {
            throw new Error('Failed to update logo');
        }
        return response.data;
    }
    /**
     * Update the vendor's logo URL (raw response)
     */
    updateLogoRaw(url) {
        return this.put('logo', { url });
    }
    /**
     * Remove the vendor's logo
     * DELETE /api/vendor-portal/profile/logo
     */
    async removeLogo() {
        const response = await this.removeLogoRaw();
        if (!response.data) {
            throw new Error('Failed to remove logo');
        }
        return response.data;
    }
    /**
     * Remove the vendor's logo (raw response)
     */
    removeLogoRaw() {
        return this.delete('logo');
    }
    // ==================== BANNER MANAGEMENT ====================
    /**
     * Update the vendor's banner URL
     * PUT /api/vendor-portal/profile/banner
     */
    async updateBanner(url) {
        const response = await this.updateBannerRaw(url);
        if (!response.data) {
            throw new Error('Failed to update banner');
        }
        return response.data;
    }
    /**
     * Update the vendor's banner URL (raw response)
     */
    updateBannerRaw(url) {
        return this.put('banner', { url });
    }
    /**
     * Remove the vendor's banner
     * DELETE /api/vendor-portal/profile/banner
     */
    async removeBanner() {
        const response = await this.removeBannerRaw();
        if (!response.data) {
            throw new Error('Failed to remove banner');
        }
        return response.data;
    }
    /**
     * Remove the vendor's banner (raw response)
     */
    removeBannerRaw() {
        return this.delete('banner');
    }
}
VendorPortalProfileService.DEFAULT_SERVICE_PATH = 'vendors';
VendorPortalProfileService.BASE_PATH = 'api/vendor-portal/profile';
//# sourceMappingURL=VendorPortalProfileService.js.map